import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import useNavigateWithParams from '../../hooks/useNavigateWithParams';
import { useGuide } from '../../context/GuideContext';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import GuideForm from '../../components/GuideForm/GuideForm';
import StudyForm from '../../components/StudyForm/StudyForm';
import LivePreview from '../../components/LivePreview/LivePreview';
import StudyNavBar from '../../components/StudyNavBar/StudyNavBar';
import ThemeToggle from '../../components/ThemeToggle/ThemeToggle';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { generatePreviewThumbnail } from '../../utils/thumbnailGenerator';
import api from '../../services/api';
import therapeuticAreaConfig from '../../config/therapeuticAreaConfig.json';
import './Builder.css';

const Builder = () => {
  const { id } = useParams();
  const navigate = useNavigateWithParams();
  const [searchParams] = useSearchParams();
  const { user, token, isGuestUser, jwtToken } = useAuth();
  const {
    currentGuide,
    setCurrentGuide,
    createNewGuide,
    saveGuide,
    publishGuide,
    addStudy,
    updateStudy,
    removeStudy,
    reorderStudies,
    drafts,
  } = useGuide();

  const [activeTab, setActiveTab] = useState('builder');
  const [activeSection, setActiveSection] = useState('guide');
  const [expandedStudy, setExpandedStudy] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [guideProgress, setGuideProgress] = useState('in_progress');
  const [currentPage, setCurrentPage] = useState(0);
  const [draggedStudyIndex, setDraggedStudyIndex] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const [popup, setPopup] = useState({ show: false, type: '', message: '' });
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Use guide's pdf_theme from database, default to 'light'
  const theme = currentGuide?.pdf_theme || 'light';
  const viewOnly = searchParams.get('view') === 'true';
  // Custom toggle function that updates the guide and triggers auto-save
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';

    // In view-only mode, just update local state without saving
    if (viewOnly) {
      setCurrentGuide({
        ...currentGuide,
        pdf_theme: newTheme,
      });
      return;
    }

    handleGuideUpdate('pdf_theme', newTheme);
    // Trigger auto-save after a short delay to ensure state update completes
    setTimeout(() => handleBlurAutoSave(), 100);
  };
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showDeleteStudyModal, setShowDeleteStudyModal] = useState(false);
  const [studyToDelete, setStudyToDelete] = useState(null);
  const [lastSavedTime, setLastSavedTime] = useState(null);
  const [lastSavedText, setLastSavedText] = useState('');
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false); // Track if user clicked save button for guide form
  const [studySaveAttempts, setStudySaveAttempts] = useState({}); // Track save attempts per study { studyId: true }
  const previewRef = useRef(null);
  const hasExportedRef = useRef(false);

  const isNewGuide = id === 'new';
  const shouldExport = searchParams.get('export') === 'true';


  // Total pages = number of studies + 1 (home/Event Details page)
  const totalPages = currentGuide ? (currentGuide.studies?.length || 0) + 1 : 1;

  const ProgressIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
  <g clip-path="url(#clip0_18201_3781)">
  <path d="M4 16C4 17.5759 4.31039 19.1363 4.91345 20.5922C5.5165 22.0481 6.40042 23.371 7.51472 24.4853C8.62902 25.5996 9.95189 26.4835 11.4078 27.0866C12.8637 27.6896 14.4241 28 16 28C17.5759 28 19.1363 27.6896 20.5922 27.0866C22.0481 26.4835 23.371 25.5996 24.4853 24.4853C25.5996 23.371 26.4835 22.0481 27.0866 20.5922C27.6896 19.1363 28 17.5759 28 16C28 12.8174 26.7357 9.76516 24.4853 7.51472C22.2348 5.26428 19.1826 4 16 4C12.8174 4 9.76516 5.26428 7.51472 7.51472C5.26428 9.76516 4 12.8174 4 16Z" stroke="#DA0000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M16 10.666V15.9993" stroke="#DA0000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M16 21.334H16.0133" stroke="#DA0000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </g>
  <defs>
  <clipPath id="clip0_18201_3781">
  <rect width="32" height="32" fill="white"/>
  </clipPath>
  </defs>
  </svg>
);

const CompleteIcon = () => (
  <svg
    className="status-complete-icon"
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_18001_2783)">
      <path
        d="M4 16C4 17.5759 4.31039 19.1363 4.91345 20.5922C5.5165 22.0481 6.40042 23.371 7.51472 24.4853C8.62902 25.5996 9.95189 26.4835 11.4078 27.0866C12.8637 27.6896 14.4241 28 16 28C17.5759 28 19.1363 27.6896 20.5922 27.0866C22.0481 26.4835 23.371 25.5996 24.4853 24.4853C25.5996 23.371 26.4835 22.0481 27.0866 20.5922C27.6896 19.1363 28 17.5759 28 16C28 14.4241 27.6896 12.8637 27.0866 11.4078C26.4835 9.95189 25.5996 8.62902 24.4853 7.51472C23.371 6.40042 22.0481 5.5165 20.5922 4.91345C19.1363 4.31039 17.5759 4 16 4C14.4241 4 12.8637 4.31039 11.4078 4.91345C9.95189 5.5165 8.62902 6.40042 7.51472 7.51472C6.40042 8.62902 5.5165 9.95189 4.91345 11.4078C4.31039 12.8637 4 14.4241 4 16Z"
        stroke="#00AE23"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 16.0007L14.6667 18.6673L20 13.334"
        stroke="#00AE23"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_18001_2783">
        <rect width="32" height="32" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

  useEffect(() => {
    const loadGuide = async () => {
      if (isNewGuide) {
        if (!currentGuide) {
          createNewGuide();
        }
        setIsLoading(false);
      } else if (token && id) {
        // For existing guides, always fetch from API to ensure we have the latest data
        // This is especially important on page refresh when drafts/publishedGuides may be empty
        // Also important when navigating from dashboard to view page - ensure we show loader
        // until the correct guide data is fetched (not stale data from context)
        setIsLoading(true);
        // Clear current guide to prevent showing stale data while loading
        setCurrentGuide(null);
        try {
          const fetchedGuide = await api.getGuide(token, jwtToken, id, user, isGuestUser);
          if (fetchedGuide) {
            setCurrentGuide(fetchedGuide);
          }
        } catch (error) {
          console.error('❌ Error fetching guide:', error);
          // Redirect to dashboard on error
          setTimeout(() => navigate('/dashboard'), 2000);
        } finally {
          setIsLoading(false);
        }
      } else {
        console.log('⏭️ Skipping guide load - token:', !!token, 'id:', id);
        setIsLoading(false);
      }
    };

    loadGuide();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNewGuide, token, jwtToken, user, isGuestUser]);

  useEffect(() => {
    if (shouldExport && currentGuide && !hasExportedRef.current) {
      hasExportedRef.current = true;
      handleExportPDF();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldExport, currentGuide]);

  // Helper function to check if a Quill field is empty
  const isQuillFieldEmpty = (html) => {
    if (!html) return true;
    const cleaned = html
      .replace(/<p[^>]*>/gi, '')
      .replace(/<\/p>/gi, '')
      .replace(/<br\s*\/?>/gi, '')
      .replace(/&nbsp;/gi, '')
      .replace(/\s+/g, '')
      .trim();
    return cleaned.length === 0;
  };

  // Helper function to validate URL format
  const isValidURL = (string) => {
    if (!string || string.trim() === '') return false;
    try {
      const url = new URL(string);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  // Helper function to check if all required guide fields are filled
  const isGuideComplete = (guide) => {
    if (!guide) return false;

    // Check therapeutic area (select field)
    if (!guide.therapeutic_area) return false;

    // Check required Quill fields
    if (isQuillFieldEmpty(guide.title)) return false;
    if (isQuillFieldEmpty(guide.event_location)) return false;
    if (isQuillFieldEmpty(guide.disclaimers)) return false;

    return true;
  };

  // Helper function to check if a study has all required fields filled
  const isStudyFieldsComplete = (study) => {
    if (!study) return false;

    // Check required fields for study
    if (!study.type) return false;
    if (isQuillFieldEmpty(study.name)) return false;
    if (!study.icon) return false;
    if (isQuillFieldEmpty(study.title)) return false;
    if (isQuillFieldEmpty(study.author_name)) return false;
    if (isQuillFieldEmpty(study.objective)) return false;
    if (isQuillFieldEmpty(study.blurb)) return false;
    if (isQuillFieldEmpty(study.references)) return false;
    // Check URL fields are not empty AND are valid URLs
    if (!isValidURL(study.presentation_link)) return false;
    if (!isValidURL(study.prescribing_link)) return false;

    return true;
  };

  // Check if all studies in the guide are complete (at least one study is required)
  const areAllStudiesComplete = (guide) => {
    if (!guide || !guide.studies || guide.studies.length === 0) return false;
    return guide.studies.every(study => isStudyFieldsComplete(study));
  };

  // Get validation error message for publishing
  const getPublishValidationError = (guide) => {
    if (!guide) return 'No guide to publish.';

    // Check guide fields
    const missingGuideFields = [];
    if (!guide.therapeutic_area) missingGuideFields.push('Therapeutic Area');
    if (isQuillFieldEmpty(guide.title)) missingGuideFields.push('Quick Guide Title');
    if (isQuillFieldEmpty(guide.event_location)) missingGuideFields.push('Event Name & Location');
    if (isQuillFieldEmpty(guide.disclaimers)) missingGuideFields.push('Disclaimer(s)');

    // Check study fields
    const incompleteStudies = [];
    if (guide.studies && guide.studies.length > 0) {
      guide.studies.forEach((study, index) => {
        const missingFields = [];
        if (!study.type) missingFields.push('Study type');
        if (isQuillFieldEmpty(study.name)) missingFields.push('Study Name');
        if (!study.icon) missingFields.push('Study Icon');
        if (isQuillFieldEmpty(study.title)) missingFields.push('Study Title');
        if (isQuillFieldEmpty(study.author_name)) missingFields.push('Study Author Name(s) and Credentials');
        if (isQuillFieldEmpty(study.objective)) missingFields.push('Objective');
        if (isQuillFieldEmpty(study.blurb)) missingFields.push('Summary of Results');
        if (isQuillFieldEmpty(study.references)) missingFields.push('References');
        // Check URL fields - must be present AND valid
        if (!study.presentation_link || study.presentation_link.trim() === '') {
          missingFields.push('Presentation Link');
        } else if (!isValidURL(study.presentation_link)) {
          missingFields.push('Presentation Link (invalid URL)');
        }
        if (!study.prescribing_link || study.prescribing_link.trim() === '') {
          missingFields.push('Prescribing Information Link');
        } else if (!isValidURL(study.prescribing_link)) {
          missingFields.push('Prescribing Information Link (invalid URL)');
        }

        if (missingFields.length > 0) {
          incompleteStudies.push(`Study ${index + 1}: ${missingFields.join(', ')}`);
        }
      });
    }

    // Build error message
    let errorMessage = 'Please fill in all required fields before publishing:\n\n';
    if (missingGuideFields.length > 0) {
      errorMessage += `Event Details: ${missingGuideFields.join(', ')}\n\n`;
    }
    if (!guide.studies || guide.studies.length === 0) {
      errorMessage += 'At least one study is required to publish the guide.\n\n';
    } else if (incompleteStudies.length > 0) {
      errorMessage += incompleteStudies.join('\n');
    }

    return errorMessage;
  };

  // Update guide progress when currentGuide changes
  useEffect(() => {
    if (currentGuide) {
      // Guide is complete only if ALL required fields are filled
      const complete = isGuideComplete(currentGuide);
      setGuideProgress(complete ? 'complete' : 'in_progress');

      // Set initial last saved time from guide's updated_at timestamp
      if (currentGuide.updated_at && !lastSavedTime) {
        setLastSavedTime(new Date(currentGuide.updated_at));
      }

      // Initialize lastSavedGuideRef when guide is loaded
      // This prevents false "changes detected" on first blur
      if (currentGuide.id && currentGuide.updated_at && !lastSavedGuideRef.current) {
        lastSavedGuideRef.current = currentGuide;
      }
    }
  }, [currentGuide, isNewGuide, lastSavedTime]);

  // Note: Interval-based auto-save removed in favor of blur-triggered auto-save
  // See handleBlurAutoSave implementation below

  // Update "Last saved" text every 10 seconds
  useEffect(() => {
    const formatTimeDifference = (date) => {
      if (!date) return '';

      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);

      if (diffInSeconds < 60) {
        return 'Last saved a few seconds ago';
      }

      const diffInMinutes = Math.floor(diffInSeconds / 60);
      if (diffInMinutes === 1) {
        return 'Last saved 1 minute ago';
      }
      if (diffInMinutes < 60) {
        return `Last saved ${diffInMinutes} minutes ago`;
      }

      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours === 1) {
        return 'Last saved 1 hour ago';
      }
      return `Last saved ${diffInHours} hours ago`;
    };

    if (lastSavedTime) {
      setLastSavedText(formatTimeDifference(lastSavedTime));

      // Update the text every 10 seconds
      const timer = setInterval(() => {
        setLastSavedText(formatTimeDifference(lastSavedTime));
      }, 10000);

      return () => clearInterval(timer);
    }
  }, [lastSavedTime]);

  // Debounced auto-save for blur events
  const blurAutoSaveTimerRef = useRef(null);
  const currentGuideRef = useRef(currentGuide);
  const lastSavedGuideRef = useRef(null);

  // Keep ref in sync with latest guide data
  useEffect(() => {
    currentGuideRef.current = currentGuide;
  }, [currentGuide]);

  const handleBlurAutoSave = useCallback(() => {
    // Clear any existing timer
    if (blurAutoSaveTimerRef.current) {
      clearTimeout(blurAutoSaveTimerRef.current);
    }

    // Debounce: wait 500ms after last blur before saving
    blurAutoSaveTimerRef.current = setTimeout(async () => {
      const latestGuide = currentGuideRef.current;

      // Skip auto-save if guide is published, currently saving, or during reorder
      if (!latestGuide || latestGuide.is_published || isSaving || isReordering) {
        return;
      }

      // Skip auto-save on /builder/new route - only auto-save on /builder/:id
      if (isNewGuide) {
        console.log('Skipping blur auto-save: on /builder/new route');
        return;
      }

      // Check if data has actually changed since last auto-save
      // Only compare user-editable fields, not timestamps or system fields
      if (lastSavedGuideRef.current) {
        const fieldsToCompare = [
          'title', 'therapeutic_area', 'header_option', 'event_location',
          'disclaimers', 'include_medically', 'medically_url', 'genentech_access_text',
          'show_society_links', 'society_label_text', 'show_author_panel',
          'author_panel_title', 'authors', 'studies',
          'society1_name', 'society1_url', 'society2_name', 'society2_url',
          'project_code', 'pdf_theme'
        ];

        const hasChanges = fieldsToCompare.some(field => {
          const currentValue = JSON.stringify(latestGuide[field]);
          const lastSavedValue = JSON.stringify(lastSavedGuideRef.current[field]);
          return currentValue !== lastSavedValue;
        });

        if (!hasChanges) {
          console.log('Skipping blur auto-save: no changes detected');
          return;
        }
      }

      try {
        console.log('Blur auto-saving guide...');

        // For blur auto-save, we save silently without updating UI state
        // This prevents the form from re-rendering and flickering
        // If current user owns this guide, use their name; otherwise preserve existing creator
        const currentUserOwnsGuide = latestGuide.user_id === user?.id;
        const currentUserName = user?.name || user?.email?.split('@')[0] || 'You';
        const authorName = currentUserOwnsGuide ? currentUserName : (latestGuide.created_by_name || currentUserName);
        const guideToSave = {
          ...latestGuide,
          created_by_name: authorName,
          updated_at: new Date().toISOString(),
        };

        // Call API directly without updating currentGuide state
        // This prevents the form fields from re-rendering
        if (latestGuide.id && token) {
          await api.updateGuide(token, jwtToken, latestGuide.id, guideToSave, user, isGuestUser);
          setLastSavedTime(new Date());
          lastSavedGuideRef.current = latestGuide;
          console.log('Blur auto-save completed');
        }
      } catch (error) {
        console.error('Blur auto-save failed:', error);
      }
    }, 500);
  }, [isSaving, isReordering, token, jwtToken, user, isGuestUser, drafts]);

  // Helper to show popup
  const showPopup = useCallback((type, message) => {
    setPopup({ show: true, type, message });
  }, []);

  // Close popup manually
  const closePopup = () => {
    setPopup({ show: false, type: '', message: '' });
    // Navigate if there's a pending navigation after popup close
    if (pendingNavigation) {
      navigate(pendingNavigation.path, pendingNavigation.options);
      setPendingNavigation(null);
    }
  };

  const handleGuideUpdate = (field, value) => {
    if (!currentGuide || currentGuide.is_published) return;
    // Only update if the value has actually changed
    // This prevents infinite loops from ReactQuill's initial onChange
    if (currentGuide[field] === value) return;

    // If therapeutic area is changing, clear study icons and reset header_option
    // This handles both updates together to avoid race conditions from separate onUpdate calls
    if (field === 'therapeutic_area' && currentGuide.therapeutic_area !== value) {
      const updatedStudies = (currentGuide.studies || []).map(study => ({
        ...study,
        icon: null, // Clear the icon so user must select a new one from the new therapeutic area's icons
      }));
      // Auto-select first header background for the new therapeutic area
      const firstHeader = therapeuticAreaConfig[value]?.headerBackgrounds?.[0];
      setCurrentGuide({
        ...currentGuide,
        therapeutic_area: value,
        header_option: firstHeader?.id || 1,
        studies: updatedStudies,
      });
      return;
    }

    // Skip header_option update if it's coming right after a therapeutic_area change
    // (the therapeutic_area handler above already sets it)
    if (field === 'header_option' && currentGuide[field] === value) return;

    setCurrentGuide({
      ...currentGuide,
      [field]: value,
    });
  };
 

  // Save to draft function
  const handleSaveDraft = async () => {
    if (!currentGuide) return;
    setIsSaving(true);
    try {
      // Step 1: Save guide without thumbnail to get guide ID
      const savedGuide = await saveGuide(currentGuide);
      if (!savedGuide) {
        showPopup('error', 'Failed to save draft. Please try again.');
        return;
      }

      const guideId = savedGuide.id;

      // Step 2: Generate and upload thumbnail separately, then save again with thumbnail_url
      const thumbnailBase64 = await generatePreviewThumbnail('live-preview-container');
      let finalGuide = savedGuide;
      if (thumbnailBase64 && token) {
        const thumbnailResponse = await api.uploadThumbnail(token, jwtToken, guideId, thumbnailBase64, user, isGuestUser);
        if (thumbnailResponse && thumbnailResponse.thumbnail_url) {
          // Save guide again with the thumbnail_url from response
          finalGuide = await saveGuide({
            ...savedGuide,
            thumbnail_url: thumbnailResponse.thumbnail_url
          });
        }
      }

      // Step 3: Update local state with final guide
      setCurrentGuide(finalGuide || savedGuide);
      setLastSavedTime(new Date());

      if (isNewGuide) {
        // Set pending navigation - will navigate after user clicks OK on popup
        setPendingNavigation({ path: `/builder/${savedGuide.id}`, options: { replace: true } });
      }
      showPopup('success', 'Draft saved successfully!');
    } catch (error) {
      console.error('Error saving draft:', error);
      showPopup('error', 'Failed to save draft. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

   const handleSaveAndContinue = async () => {
    if (!currentGuide) return;
    setIsSaving(true);
    try {
      // Step 1: Save guide without thumbnail to get guide ID
      const savedGuide = await saveGuide(currentGuide);
      if (!savedGuide) {
        showPopup('error', 'Failed to save guide. Please try again.');
        return;
      }

      const guideId = savedGuide.id;

      // Step 2: Upload thumbnail and study images
      if (token) {
        let updatedGuideData = { ...savedGuide };
        let needsUpdate = false;

        // Upload guide thumbnail
        const thumbnailBase64 = await generatePreviewThumbnail('live-preview-container');
        if (thumbnailBase64) {
          try {
            const thumbnailResponse = await api.uploadThumbnail(token, jwtToken, guideId, thumbnailBase64, user, isGuestUser);
            if (thumbnailResponse && thumbnailResponse.thumbnail_url) {
              updatedGuideData.thumbnail_url = thumbnailResponse.thumbnail_url;
              needsUpdate = true;
              console.log('✅ Guide thumbnail uploaded:', thumbnailResponse.thumbnail_url);
            }
          } catch (uploadError) {
            console.error('Error uploading guide thumbnail:', uploadError);
          }
        }

        // Upload study images that were stored as base64
        const studies = [...(savedGuide.studies || [])];
        let hasUploadedStudyImages = false;
        for (const study of studies) {
          if (study.image && study.image.startsWith('data:')) {
            try {
              const response = await api.uploadThumbnail(token, jwtToken, guideId, study.image, user, isGuestUser, true);
              if (response && response.thumbnail_url) {
                study.image = response.thumbnail_url;
                hasUploadedStudyImages = true;
              }
            } catch (uploadError) {
              console.error('Error uploading study image:', uploadError);
            }
          }
        }

        if (hasUploadedStudyImages) {
          updatedGuideData.studies = studies;
          needsUpdate = true;
        }

        // Update guide with uploaded thumbnail_url and/or study image URLs
        if (needsUpdate) {
          const finalGuide = await api.updateGuide(token, jwtToken, guideId, updatedGuideData, user, isGuestUser);
          setCurrentGuide(finalGuide);
        } else {
          setCurrentGuide(savedGuide);
        }
      } else {
        setCurrentGuide(savedGuide);
      }

      // Step 3: Update local state
      setLastSavedTime(new Date());
      // Update progress based on whether all required fields are filled
      setGuideProgress(isGuideComplete(savedGuide) ? 'complete' : 'in_progress');

      if (isNewGuide) {
        // Set pending navigation - will navigate after user clicks OK on popup
        setPendingNavigation({ path: `/builder/${savedGuide.id}`, options: { replace: true } });
      }
      showPopup('success', 'Guide saved successfully!');
    } catch (error) {
      console.error('Error saving guide:', error);
      showPopup('error', 'Failed to save guide. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddStudy = () => {
    const newStudy = addStudy({
      title: '',
      type: 'LTE',
      references: '',
      abbreviations: '',
      prescribing_link: '',
      msl_contact_link: '',
      displayIndex: (currentGuide?.studies?.length || 0) + 1,
    });
    if (newStudy) {
      setExpandedStudy(newStudy.id);
      setActiveSection(newStudy.id);
      // Navigate to the new study page in preview (page 0 is home, so studies start at page 1)
      const newStudyIndex = (currentGuide?.studies?.length || 0);
      setCurrentPage(newStudyIndex + 1); // +1 because page 0 is home page
    }
  };

  const handleStudyUpdate = (studyId, field, value) => {
    if (currentGuide?.is_published) return;
    updateStudy(studyId, { [field]: value });
  };

  // Check if all mandatory study fields are populated (aligned with isStudyFieldsComplete for publishing)
  const isStudyComplete = (study) => {
    if (!study) return false;

    // Check required fields for study
    if (!study.type) return false;
    if (isQuillFieldEmpty(study.name)) return false;
    if (!study.icon) return false;
    if (isQuillFieldEmpty(study.title)) return false;
    if (isQuillFieldEmpty(study.author_name)) return false;
    if (isQuillFieldEmpty(study.objective)) return false;
    if (isQuillFieldEmpty(study.blurb)) return false;
    if (isQuillFieldEmpty(study.references)) return false;
    // Check URL fields are not empty AND are valid URLs
    if (!isValidURL(study.presentation_link)) return false;
    if (!isValidURL(study.prescribing_link)) return false;

    return true;
  };

  const handleStudySave = async (studyId) => {
    const studies = currentGuide?.studies || [];
    const study = studies.find(s => s.id === studyId);
    if (study) {
      // Only mark as complete if all mandatory fields are filled
      const complete = isStudyComplete(study);
      updateStudy(studyId, { isComplete: complete });
      // Save the guide and show success popup
      setIsSaving(true);
      try {
        // Step 1: Save guide without thumbnail to get guide ID
        const savedGuide = await saveGuide(currentGuide);
        if (!savedGuide) {
          showPopup('error', 'Failed to save study. Please try again.');
          return;
        }

        const guideId = savedGuide.id;

        // Step 2: Upload thumbnail and study images
        if (token) {
          let updatedGuideData = { ...savedGuide };
          let needsUpdate = false;

          // Upload guide thumbnail
          const thumbnailBase64 = await generatePreviewThumbnail('live-preview-container');
          if (thumbnailBase64) {
            try {
              const thumbnailResponse = await api.uploadThumbnail(token, jwtToken, guideId, thumbnailBase64, user, isGuestUser);
              if (thumbnailResponse && thumbnailResponse.thumbnail_url) {
                updatedGuideData.thumbnail_url = thumbnailResponse.thumbnail_url;
                needsUpdate = true;
                console.log('✅ Guide thumbnail uploaded:', thumbnailResponse.thumbnail_url);
              }
            } catch (uploadError) {
              console.error('Error uploading guide thumbnail:', uploadError);
            }
          }

          // Upload study images that were stored as base64 (only for new guides)
          if (isNewGuide) {
            const studiesList = [...(savedGuide.studies || [])];
            let hasUploadedStudyImages = false;
            for (const s of studiesList) {
              if (s.image && s.image.startsWith('data:')) {
                try {
                  const response = await api.uploadThumbnail(token, jwtToken, guideId, s.image, user, isGuestUser, true);
                  if (response && response.thumbnail_url) {
                    s.image = response.thumbnail_url;
                    hasUploadedStudyImages = true;
                  }
                } catch (uploadError) {
                  console.error('Error uploading study image:', uploadError);
                }
              }
            }

            if (hasUploadedStudyImages) {
              updatedGuideData.studies = studiesList;
              needsUpdate = true;
            }
          }

          // Update guide with uploaded thumbnail_url and/or study image URLs
          if (needsUpdate) {
            const finalGuide = await api.updateGuide(token, jwtToken, guideId, updatedGuideData, user, isGuestUser);
            setCurrentGuide(finalGuide);
          } else {
            setCurrentGuide(savedGuide);
          }
        } else {
          setCurrentGuide(savedGuide);
        }

        setLastSavedTime(new Date());

        if (isNewGuide) {
          // Set pending navigation - will navigate after user clicks OK on popup
          setPendingNavigation({ path: `/builder/${savedGuide.id}`, options: { replace: true } });
        }
        showPopup('success', 'Study saved successfully!');
      } catch (error) {
        console.error('Error saving study:', error);
        showPopup('error', 'Failed to save study. Please try again.');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleStudyDelete = (studyId) => {
    setStudyToDelete(studyId);
    setShowDeleteStudyModal(true);
  };

  const confirmStudyDelete = () => {
    if (studyToDelete) {
      // Find the index of the study being deleted
      const studyIndex = currentGuide?.studies.findIndex(s => s.id === studyToDelete);

      // Remove the study
      removeStudy(studyToDelete);

      // Handle preview page navigation after deletion
      if (studyIndex !== -1) {
        const studyPageIndex = studyIndex + 1; // +1 because page 0 is home

        // If we're currently viewing the deleted study or a page after it
        if (currentPage === studyPageIndex) {
          // Navigate to home page if deleting the only study, otherwise navigate to previous study or home
          const newStudiesCount = (currentGuide?.studies?.length || 1) - 1;
          if (newStudiesCount === 0) {
            setCurrentPage(0); // Go to home page
          } else if (studyIndex > 0) {
            setCurrentPage(studyPageIndex - 1); // Go to previous study
          } else {
            setCurrentPage(1); // Go to first study (if deleting the first one)
          }
        } else if (currentPage > studyPageIndex) {
          // If viewing a page after the deleted study, adjust the page index
          setCurrentPage(currentPage - 1);
        }
      }

      // Handle builder accordion state
      if (expandedStudy === studyToDelete) {
        setExpandedStudy(null);
        setActiveSection('guide');
      }

      setStudyToDelete(null);
      setShowDeleteStudyModal(false);
    }
  };

  const cancelStudyDelete = () => {
    setStudyToDelete(null);
    setShowDeleteStudyModal(false);
  };

  const isInteractiveTarget = (target) => {
    if (!target) return false;

    return !!target.closest(
      'input, textarea, select, option, button, a, label,' +
      ' [contenteditable="true"], .ql-editor, .ql-toolbar, .quill-wrapper,' +
      ' .file-input, .choose-file-btn'
    );
  };

  const handleStudyClick = (studyId) => {
    setExpandedStudy(expandedStudy === studyId ? null : studyId);
    setActiveSection(studyId);

    // Navigate to corresponding page in preview (page 0 is home, studies start at page 1)
    const studyIndex = currentGuide?.studies.findIndex(s => s.id === studyId);
    if (studyIndex !== -1) {
      setCurrentPage(studyIndex + 1); // +1 because page 0 is home page
    }
  };

  // Drag and Drop handlers for study reordering
  const handleDragStart = (e, index) => {
  setDraggedStudyIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedStudyIndex === null || draggedStudyIndex === dropIndex) {
      setDraggedStudyIndex(null);
      return;
    }

    const studies = [...(currentGuide?.studies || [])];
    if (studies.length === 0) {
      setDraggedStudyIndex(null);
      return;
    }

    setIsReordering(true);
    const draggedStudy = studies[draggedStudyIndex];
    studies.splice(draggedStudyIndex, 1);
    studies.splice(dropIndex, 0, draggedStudy);

    const newOrder = studies.map(s => s.id);
    reorderStudies(newOrder);

    // Update preview page to follow the dragged study
    if (expandedStudy === draggedStudy.id) {
      setCurrentPage(dropIndex + 1); // +1 because page 0 is home page
    }

    setDraggedStudyIndex(null);
    // Reset reordering flag after a short delay to allow state to settle
    setTimeout(() => setIsReordering(false), 100);
  };

  const handleDragEnd = () => {
    setDraggedStudyIndex(null);
  };

  // Move study up in the list
  const handleMoveStudyUp = (index) => {
    if (index <= 0 || currentGuide?.is_published) return;
    setIsReordering(true);
    const studies = [...(currentGuide?.studies || [])];
    const movedStudy = studies[index];
    const temp = studies[index];
    studies[index] = studies[index - 1];
    studies[index - 1] = temp;
    const newOrder = studies.map(s => s.id);
    reorderStudies(newOrder);

    // Update preview page to follow the moved study
    if (expandedStudy === movedStudy.id) {
      setCurrentPage(index); // New position is index - 1 + 1 (for home page) = index
    }

    // Reset reordering flag after a short delay to allow state to settle
    setTimeout(() => setIsReordering(false), 100);
  };

  // Move study down in the list
  const handleMoveStudyDown = (index) => {
    const studies = currentGuide?.studies || [];
    if (index >= studies.length - 1 || currentGuide?.is_published) return;
    setIsReordering(true);
    const studiesCopy = [...studies];
    const movedStudy = studiesCopy[index];
    const temp = studiesCopy[index];
    studiesCopy[index] = studiesCopy[index + 1];
    studiesCopy[index + 1] = temp;
    const newOrder = studiesCopy.map(s => s.id);
    reorderStudies(newOrder);

    // Update preview page to follow the moved study
    if (expandedStudy === movedStudy.id) {
      setCurrentPage(index + 2); // New position is index + 1 + 1 (for home page) = index + 2
    }

    // Reset reordering flag after a short delay to allow state to settle
    setTimeout(() => setIsReordering(false), 100);
  };

  const handlePublish = () => {
    if (!currentGuide) return;

    // Validate guide and studies before showing publish modal
    const isGuideValid = isGuideComplete(currentGuide);
    const areStudiesValid = areAllStudiesComplete(currentGuide);

    if (!isGuideValid || !areStudiesValid) {
      const errorMessage = getPublishValidationError(currentGuide);
      showPopup('error', errorMessage);
      return;
    }

    setShowPublishModal(true);
  };

  const confirmPublish = async () => {
    setShowPublishModal(false);
    setIsSaving(true);
    try {
      // Step 1: Save guide without thumbnail to get guide ID
      const savedGuide = await saveGuide(currentGuide);
      if (!savedGuide) {
        showPopup('error', 'Failed to save guide before publishing. Please try again.');
        return;
      }

      const guideId = savedGuide.id;

      // Step 2: Generate and upload thumbnail, then update guide with thumbnail_url
      const thumbnailBase64 = await generatePreviewThumbnail('live-preview-container');
      if (thumbnailBase64 && token) {
        try {
          const thumbnailResponse = await api.uploadThumbnail(token, jwtToken, guideId, thumbnailBase64, user, isGuestUser);
          if (thumbnailResponse && thumbnailResponse.thumbnail_url) {
            // Update guide with thumbnail_url before publishing
            await api.updateGuide(token, jwtToken, guideId, { ...savedGuide, thumbnail_url: thumbnailResponse.thumbnail_url }, user, isGuestUser);
            console.log('✅ Guide thumbnail uploaded before publish:', thumbnailResponse.thumbnail_url);
          }
        } catch (uploadError) {
          console.error('Error uploading guide thumbnail:', uploadError);
        }
      }

      // Step 3: Publish the guide
      const published = await publishGuide(guideId);
      if (published) {
        setPendingNavigation({ path: '/dashboard', options: { replace: false, state: { refresh: true } } });
        showPopup('success', 'Guide published successfully!');
      } else {
        showPopup('error', 'Failed to publish guide. Please try again.');
      }
    } catch (error) {
      console.error('Error publishing guide:', error);
      showPopup('error', 'Failed to publish guide. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const cancelPublish = () => {
    setShowPublishModal(false);
  };

  const handleExportPDF = useCallback(async () => {
  if (!previewRef.current || !currentGuide) return;
 
  setIsSaving(true);
 
  const livePreviewEl = previewRef.current.querySelector('.live-preview');
  const pages = previewRef.current.querySelectorAll('.preview-page');
  const originalStyles = [];
 
  try {
    if (livePreviewEl) {
      livePreviewEl.classList.add('exporting');
    }
 
    if (pages.length === 0) {
      showPopup('error', 'No study pages found. Add at least one study to export.');
      return;
    }
 
    const pdfWidth = 210;
    const originalLivePreviewStyle = livePreviewEl?.style.cssText || '';
 
    // Get the actual width of the first preview page to match PDF export
    const firstPage = pages[0];
    const actualWidth = firstPage ? firstPage.getBoundingClientRect().width : 620;
    const exportWidth = `${Math.round(actualWidth)}px`;

    if (livePreviewEl) {
      livePreviewEl.style.width = "665px";
      livePreviewEl.style.minWidth = "665px";
    }

    pages.forEach((page) => {
      originalStyles.push({
        className: page.className,
        display: page.style.display,
        visibility: page.style.visibility,
        width: page.style.width,
        minWidth: page.style.minWidth,
      });

      page.classList.remove('hidden-for-slider');
      page.classList.add('visible');
      page.style.display = 'flex';
      page.style.visibility = 'visible';
      page.style.width = "665px";
      page.style.minWidth = "665px";
    });
 
    await new Promise(r => setTimeout(r, 300));

    // Add "..." to truncated titles for PDF export
    const ellipsisElements = [];
    pages.forEach((page) => {
      const titleElements = page.querySelectorAll('.page-header-title');
      titleElements.forEach((el) => {
        // Check if text is being truncated by CSS line-clamp
        if (el.scrollHeight > el.clientHeight + 2) {
          // Get the bounding rect of the element
          const elRect = el.getBoundingClientRect();
          const computedStyle = window.getComputedStyle(el);

          // Create a range to find where the last visible text ends
          const range = document.createRange();
          range.selectNodeContents(el);
          const rects = range.getClientRects();

          // Get the last visible rect (last line of text)
          let lastVisibleRect = null;
          const elBottom = elRect.top + el.clientHeight;

          for (let i = rects.length - 1; i >= 0; i--) {
            if (rects[i].bottom <= elBottom + 5) {
              lastVisibleRect = rects[i];
              break;
            }
          }

          if (lastVisibleRect) {
            // Create ellipsis span positioned at the end of the last line
            const ellipsisSpan = document.createElement('span');
            ellipsisSpan.textContent = '...';
            ellipsisSpan.style.cssText = `
              position: absolute;
              left: ${lastVisibleRect.right - elRect.left}px;
              top: ${lastVisibleRect.top - elRect.top}px;
              color: #ffffff;
              font-size: ${computedStyle.fontSize};
              font-weight: ${computedStyle.fontWeight};
              text-transform: uppercase;
              letter-spacing: 0.5px;
              line-height: ${computedStyle.lineHeight};
              z-index: 1;
            `;

            // Make parent relative for positioning
            const originalPosition = el.style.position;
            el.style.position = 'relative';
            el.appendChild(ellipsisSpan);

            ellipsisElements.push({ el, ellipsisSpan, originalPosition });
          }
        }
      });
    });

    await new Promise(r => setTimeout(r, 50));

    const pageCanvases = [];
    const pageHeights = [];
    const studyStartPages = [];
    const allPdfLinks = [];
    let pdf = null;
 
    for (let i = 0; i < pages.length; i++) {
      const page = pages[i];
      const rect = page.getBoundingClientRect();

      const canvas = await html2canvas(page, {
        scale: 3,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
      });

      pageCanvases.push({ canvas, rect });
      pageHeights.push((canvas.height * pdfWidth) / canvas.width);
    }

    for (let i = 0; i < pages.length; i++) {
      const { canvas, rect } = pageCanvases[i];
      const height = pageHeights[i];
      const scaleFactor = pdfWidth / rect.width;

      if (i === 0) {
        pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: [pdfWidth, height] });
      } else {
        pdf.addPage([pdfWidth, height]);
      }

      studyStartPages[i] = i + 1;
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pdfWidth, height);
 
      const navItems = [];
      const externalLinks = [];
 
      pages[i].querySelectorAll('.pdf-nav-item').forEach(item => {
        const r = item.getBoundingClientRect();
        navItems.push({
          left: r.left - rect.left,
          top: r.top - rect.top,
          width: r.width,
          height: r.height,
          targetIndex: parseInt(item.dataset.pageIndex, 10),
        });
      });
 
      pages[i].querySelectorAll('.pdf-external-link').forEach(link => {
        const r = link.getBoundingClientRect();
        if (link.href) {
          externalLinks.push({
            left: r.left - rect.left,
            top: r.top - rect.top,
            width: r.width,
            height: r.height,
            url: link.href,
          });
        }
      });
 
      allPdfLinks.push({
        page: i + 1,
        navItems,
        externalLinks,
        scaleFactor,
      });
    }
 
    const isValid = (...n) => n.every(v => Number.isFinite(v) && v >= 0);
 
    for (const pageLinks of allPdfLinks) {
      const { page, navItems, externalLinks, scaleFactor } = pageLinks;
 
      pdf.setPage(page);
 
      navItems.forEach(item => {
        const targetPage = studyStartPages[item.targetIndex];
        if (!targetPage || targetPage === page) return;
 
        const x = item.left * scaleFactor;
        const y = item.top * scaleFactor;
        const w = item.width * scaleFactor;
        const h = item.height * scaleFactor;
 
        if (!isValid(x, y, w, h) || w === 0 || h === 0) return;
 
        pdf.link(x, y, w, h, {
          pageNumber: targetPage,
          top: 0, // ✅ Adobe-safe
        });
      });
 
      externalLinks.forEach(link => {
        const x = link.left * scaleFactor;
        const y = link.top * scaleFactor;
        const w = link.width * scaleFactor;
        const h = link.height * scaleFactor;
 
        if (!isValid(x, y, w, h) || w === 0 || h === 0) return;
 
        pdf.link(x, y, w, h, { url: link.url });
      });
    }
 
    pages.forEach((page, i) => {
      const o = originalStyles[i];
      page.className = o.className;
      page.style.display = o.display;
      page.style.visibility = o.visibility;
      page.style.width = o.width;
      page.style.minWidth = o.minWidth;
    });
 
    if (livePreviewEl) {
      livePreviewEl.classList.remove('exporting');
      livePreviewEl.style.cssText = originalLivePreviewStyle;
    }

    // Remove ellipsis spans added for export
    ellipsisElements.forEach(({ el, ellipsisSpan, originalPosition }) => {
      if (ellipsisSpan && ellipsisSpan.parentNode === el) {
        el.removeChild(ellipsisSpan);
      }
      el.style.position = originalPosition || '';
    });

    // Generate unique filename with timestamp
    const timestamp = Math.floor(Date.now() / 1000);
    // Strip HTML tags and clean the title for filename
    const rawTitle = currentGuide?.title || 'guide';
    const guideTitle = rawTitle
      .replace(/<[^>]*>/g, '') // Remove all HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
      .replace(/&amp;/g, '&')  // Replace &amp; with &
      .replace(/&lt;/g, '<')   // Replace &lt; with <
      .replace(/&gt;/g, '>')   // Replace &gt; with >
      .replace(/[<>:"/\\|?*]/g, '') // Remove invalid filename characters
      .replace(/\s+/g, '_')    // Replace spaces with underscores
      .trim()
      .substring(0, 50) || 'guide'; // Limit to 50 characters
    const filename = `${guideTitle}_${timestamp}.pdf`;

    pdf.save(filename);

    // If export was triggered from dashboard (via URL param), navigate back
    if (shouldExport) {
      navigate('/dashboard', { replace: true, state: { refresh: true } });
    } else {
      showPopup('success', 'PDF exported successfully!');
    }
  } catch (e) {
    console.error(e);
    showPopup('error', 'Failed to export PDF.');
  } finally {
    setIsSaving(false);
  }
}, [currentGuide, showPopup, shouldExport, navigate]);

  const handleBack = () => {
    // Navigate back to dashboard with refresh state
    navigate('/dashboard', { replace: false, state: { refresh: true } });
  };

  // Save and exit - saves everything and navigates to dashboard
  const handleSaveAndExit = async () => {
    if (!currentGuide) return;
    setIsSaving(true);
    try {
      // Step 1: Save guide without thumbnail to get guide ID
      const savedGuide = await saveGuide(currentGuide);
      if (!savedGuide) {
        showPopup('error', 'Failed to save guide. Please try again.');
        return;
      }

      const guideId = savedGuide.id;

      // Step 2: Upload thumbnail and study images
      if (token) {
        let updatedGuideData = { ...savedGuide };
        let needsUpdate = false;

        // Upload guide thumbnail
        const thumbnailBase64 = await generatePreviewThumbnail('live-preview-container');
        if (thumbnailBase64) {
          try {
            const thumbnailResponse = await api.uploadThumbnail(token, jwtToken, guideId, thumbnailBase64, user, isGuestUser);
            if (thumbnailResponse && thumbnailResponse.thumbnail_url) {
              updatedGuideData.thumbnail_url = thumbnailResponse.thumbnail_url;
              needsUpdate = true;
              console.log('✅ Guide thumbnail uploaded:', thumbnailResponse.thumbnail_url);
            }
          } catch (uploadError) {
            console.error('Error uploading guide thumbnail:', uploadError);
          }
        }

        // Upload study images that were stored as base64 (only for new guides)
        if (isNewGuide) {
          const studies = [...(savedGuide.studies || [])];
          let hasUploadedStudyImages = false;
          for (const study of studies) {
            if (study.image && study.image.startsWith('data:')) {
              try {
                const response = await api.uploadThumbnail(token, jwtToken, guideId, study.image, user, isGuestUser, true);
                if (response && response.thumbnail_url) {
                  study.image = response.thumbnail_url;
                  hasUploadedStudyImages = true;
                }
              } catch (uploadError) {
                console.error('Error uploading study image:', uploadError);
              }
            }
          }

          if (hasUploadedStudyImages) {
            updatedGuideData.studies = studies;
            needsUpdate = true;
          }
        }

        // Update guide with uploaded thumbnail_url and/or study image URLs
        if (needsUpdate) {
          await api.updateGuide(token, jwtToken, guideId, updatedGuideData, user, isGuestUser);
        }
      }

      // Step 3: Navigate to dashboard after user clicks OK on popup
      setLastSavedTime(new Date());
      setPendingNavigation({ path: '/dashboard', options: { replace: false, state: { refresh: true } } });
      showPopup('success', 'Guide saved successfully!');
    } catch (error) {
      console.error('Error saving guide:', error);
      showPopup('error', 'Failed to save guide. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Page navigation handlers
  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToPage = (pageIndex) => {
    setCurrentPage(pageIndex);
    // Sync the study accordion with the preview page
    const studies = currentGuide?.studies || [];
    // Page 0 is home, pages 1+ are studies
    if (pageIndex === 0) {
      // Home page - expand guide section
      setExpandedStudy(null);
      setActiveSection('guide');
    } else if (pageIndex > 0 && pageIndex <= studies.length) {
      // Study page
      const studyIndex = pageIndex - 1; // -1 because page 0 is home
      const studyId = studies[studyIndex].id;
      setExpandedStudy(studyId);
      setActiveSection(studyId);
    }
  };

  const [isFormFocused, setIsFormFocused] = useState(false);

 const isQuillEmpty = (html = '') => {
  if (!html) return true;

  // Remove wrapper <p>... </p> and whitespace
  const cleaned = html
    .replace(/<p[^>]*>/gi, '')
    .replace(/<\/p>/gi, '')
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, '')
    .replace(/\s+/g, '')
    .trim();

  return cleaned.length === 0;
};

const stripOuterPTags = (html = '') => {
  return html
    .replace(/^\s*<p[^>]*>/i, '')
    .replace(/<\/p>\s*$/i, '')
    .replace(/<p><br\s*\/?><\/p>/gi, '');
};

// Check if title and event location are filled (for Exit and Save button)
const isSaveReady = currentGuide && !isQuillFieldEmpty(currentGuide.title) ;

// Check if guide event details are complete (for Export PDF - only depends on event details)
const isExportReady = currentGuide && isGuideComplete(currentGuide);

// Check if guide is ready for publishing (all required fields in guide and all studies are filled)
const isGuideReady = currentGuide && isGuideComplete(currentGuide) && areAllStudiesComplete(currentGuide);

  if (isLoading || !currentGuide) {
    return (
      <div className="builder-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className={`builder ${viewOnly ? 'view-mode' : ''}`}>
      <Header
        lastSavedText={lastSavedText}
        actions={
          !currentGuide?.is_published && (
            <button
              className="btn-exit-save"
              onClick={handleSaveAndExit}
              disabled={isSaving || !isSaveReady}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_18216_2305)">
              <path d="M4.16797 10H15.8346" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M4.16797 10L9.16797 15" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M4.16797 10L9.16797 5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </g>
              <defs>
              <clipPath id="clip0_18216_2305">
              <rect width="20" height="20" fill="white"/>
              </clipPath>
              </defs>
              </svg>
              Exit and Save
            </button>
          )
        }
      />

      <div className="builder-container">
        <div className="builder-toolbar">
        <button className="btn btn-secondary" onClick={handleBack}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12 4L6 10L12 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>
        <div className="builder-toolbar-right">
          {/* Published badge - show for published guides */}
          {currentGuide.is_published && (
            <div className="published-badge">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M4 10L8 14L16 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Published
            </div>
          )}
        </div>
      </div>

      {/* Success/Error Popup Modal */}
      {popup.show && (
        <div className="popup-overlay">
          <div className={`popup-modal ${popup.type}`}>
            {/* <div className="popup-icon">
              {popup.type === 'success' ? (
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="3"/>
                  <path d="M14 24L21 31L34 18" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="3"/>
                  <path d="M18 18L30 30M30 18L18 30" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                </svg>
              )}
            </div> */}
            <h3 className="popup-title">
              {popup.type === 'success' ? 'Success!' : 'Error'}
            </h3>
            <p className="popup-message">{popup.message}</p>
            <button className="popup-close-btn" onClick={closePopup}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Mobile Tab Navigation */}
      <div className="mobile-tabs">
        <button
          className={`mobile-tab ${activeTab === 'builder' ? 'active' : ''}`}
          onClick={() => setActiveTab('builder')}
        >
          Builder
        </button>
        <button
          className={`mobile-tab ${activeTab === 'preview' ? 'active' : ''}`}
          onClick={() => setActiveTab('preview')}
        >
          Preview
        </button>
      </div>

      <div className="builder-content">
        {/* Builder Panel */}
        <div className={`builder-panel ${activeTab === 'builder' ? 'active' : ''}`}>
          {/* Builder Nav - Hidden for now */}
          {/* <div className="builder-nav">
            <StudyNavBar
              studies={currentGuide?.studies || []}
              activeSection={activeSection}
              onSectionClick={(sectionId) => {
                if (sectionId === 'guide') {
                  setActiveSection('guide');
                  setExpandedStudy(null);
                } else {
                  handleStudyClick(sectionId);
                }
              }}
              onAddStudy={handleAddStudy}
              onReorder={reorderStudies}
            />
          </div> */}

          <div className="builder-forms">
            {/* Guide Info Section */}
            <div className={`form-section ${activeSection === 'guide' ? 'active' : ''}`}>
              <div
                className="form-section-header clickable"
                onClick={() => {
                  setActiveSection(activeSection === 'guide' ? null : 'guide');
                  setExpandedStudy(null);
                  // Navigate to first page (Event Details page) in live preview
                  setCurrentPage(0);
                }}
              >
                <div className="form-section-title main-details-title">
                  <h3>Event Details</h3>
                  <span className={`status-badge ${guideProgress}`}>
                    {guideProgress === 'in_progress' ? (
                      <>
                        <ProgressIcon />
                        <span>Please Complete</span>
                      </>
                    ) : (
                      <>
                        <CompleteIcon />
                        <span>Complete</span>
                      </>
                    )}
                  </span>
                </div>
                <svg
                  className={`expand-icon ${activeSection === 'guide' ? 'expanded' : ''}`}
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M7 8L10 11L13 8"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              {activeSection === 'guide' && (
                <GuideForm
                  guide={currentGuide}
                  onUpdate={handleGuideUpdate}
                  onSave={handleSaveAndContinue}
                  onBlur={handleBlurAutoSave}
                  isSaving={isSaving}
                  isPublished={currentGuide?.is_published}
                  token={token}
                  jwtToken={jwtToken}
                  guideId={currentGuide?.id}
                  user={user}
                  isGuestUser={isGuestUser}
                  hasAttemptedSave={hasAttemptedSave}
                  onSaveAttempt={() => setHasAttemptedSave(true)}
                />
              )}
            </div>

            {/* Study Sections with Drag and Drop */}
            {(currentGuide?.studies || []).map((study, index) => (
              <div key={study.id} className="study-section-wrapper">
                {/* Floating Reorder Arrows */}
                {!currentGuide?.is_published && (currentGuide?.studies || []).length > 1 && (
                  <div className="study-reorder-arrows">
                    <button
                      className="reorder-arrow-btn"
                      onClick={() => handleMoveStudyUp(index)}
                      disabled={index === 0}
                      title="Move up"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clip-path="url(#clip0_18001_2652)">
                      <path d="M12 19V5" stroke="#AE2185" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M16 9L12 5" stroke="#AE2185" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M8 9L12 5" stroke="#AE2185" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </g>
                      <defs>
                      <clipPath id="clip0_18001_2652">
                      <rect width="24" height="24" fill="white" transform="matrix(1 0 0 -1 0 24)"/>
                      </clipPath>
                      </defs>
                      </svg>
                    </button>
                    <button
                      className="reorder-arrow-btn"
                      onClick={() => handleMoveStudyDown(index)}
                      disabled={index === (currentGuide?.studies || []).length - 1}
                      title="Move down"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clip-path="url(#clip0_18001_2657)">
                      <path d="M12 5V19" stroke="#AE2185" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M16 15L12 19" stroke="#AE2185" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M8 15L12 19" stroke="#AE2185" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </g>
                      <defs>
                      <clipPath id="clip0_18001_2657">
                      <rect width="24" height="24" fill="white"/>
                      </clipPath>
                      </defs>
                      </svg>
                    </button>
                  </div>
                )}
                <div
                  className={`form-section study-section ${expandedStudy === study.id ? 'expanded' : ''} ${draggedStudyIndex === index ? 'dragging' : ''} ${currentGuide?.is_published ? 'published' : ''}`}
                  draggable={!currentGuide?.is_published && !isFormFocused}
                  onDragStart={(e) => !currentGuide?.is_published && handleDragStart(e, index)}
                  onDragOver={(e) => !currentGuide?.is_published && handleDragOver(e, index)}
                  onDrop={(e) => !currentGuide?.is_published && handleDrop(e, index)}
                  onDragEnd={!currentGuide?.is_published ? handleDragEnd : undefined}
                  onFocusCapture={(e) => {
                    if (isInteractiveTarget(e.target)) setIsFormFocused(true);
                  }}
                  onBlurCapture={(e) => {
                    // if focus leaves the entire card, re-enable dragging
                    if (!e.currentTarget.contains(e.relatedTarget)) setIsFormFocused(false);
                  }}
                >
                  <div
                    className="form-section-header clickable"
                    onClick={() => handleStudyClick(study.id)}
                  >
                    <div className="form-section-title">
                    {!isQuillEmpty(study.name) ? (
                      <h3
                      dangerouslySetInnerHTML={{
                        __html: (study.name || '')
                          .match(/<p[^>]*>[\s\S]*?<\/p>/i)?.[0]           // ✅ only first <p>...</p>
                          ?.replace(/^\s*<p[^>]*>/i, '')                 // remove opening <p>
                          ?.replace(/<\/p>\s*$/i, '')                    // remove closing </p>
                          ?.replace(/<p><br\s*\/?><\/p>/gi, '')          // remove empty <p><br></p>
                          || '',
                      }}
                    />
                    ) : (
                      <h3>{`Study ${index + 1}`}</h3>
                    )}
                  </div>
                  <div className="form-section-actions">
                    {!currentGuide?.is_published && (
                      <button
                        className="btn-delete-study"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStudyDelete(study.id);
                        }}
                        title="Delete study"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M2 4H14M5.333 4V2.667C5.333 2.298 5.632 2 6 2H10C10.368 2 10.667 2.298 10.667 2.667V4M6.667 7.333V11.333M9.333 7.333V11.333M3.333 4L4 13.333C4 13.702 4.298 14 4.667 14H11.333C11.702 14 12 13.702 12 13.333L12.667 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Delete
                      </button>
                    )}
                      <span className={`status-badge ${isStudyComplete(study) ? 'complete' : 'in_progress'}`}>
                        {isStudyComplete(study) ? (
                          <>
                            <CompleteIcon />
                            <span>Complete</span>
                          </>
                        ) : (
                          <>
                            <ProgressIcon />
                            <span>Please Complete</span>
                          </>
                        )}
                      </span>

                    <svg
                      className={`expand-icon ${expandedStudy === study.id ? 'expanded' : ''}`}
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M7 8L10 11L13 8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
                {expandedStudy === study.id && (
                  <StudyForm
                    study={study}
                    onUpdate={(field, value) => handleStudyUpdate(study.id, field, value)}
                    onSave={() => handleStudySave(study.id)}
                    onDelete={() => handleStudyDelete(study.id)}
                    onBlur={handleBlurAutoSave}
                    isSaving={isSaving}
                    isPublished={currentGuide?.is_published}
                    token={token}
                    jwtToken={jwtToken}
                    guideId={currentGuide?.id}
                    user={user}
                    isGuestUser={isGuestUser}
                    isNewGuide={isNewGuide}
                    showPopup={showPopup}
                    therapeuticArea={currentGuide?.therapeutic_area}
                    hasAttemptedSave={studySaveAttempts[study.id] || false}
                    onSaveAttempt={() => setStudySaveAttempts(prev => ({ ...prev, [study.id]: true }))}
                  />
                )}
                </div>
              </div>
            ))}

            {/* Add Study Button - only show for drafts */}
            {!currentGuide?.is_published && (
              <button className="add-study-btn" onClick={handleAddStudy}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Add Study
              </button>
            )}
          </div>
        </div>

        {/* Preview Panel with Slider */}
        <div className={`preview-panel ${activeTab === 'preview' ? 'active' : ''}`}>
          <div className="preview-panel-header">
            <div className="preview-panel-header-left">
              <h3>Live Preview</h3>
              <p className="preview-subtitle">See your quick guide build as you create</p>
            </div>
            <div className="preview-panel-header-right">
              <button
                  className="btn-export-pdf"
                  onClick={handleExportPDF}
                  disabled={isSaving || !isExportReady}
                >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 3V13M10 13L6 9M10 13L14 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 15V17H17V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Export PDF
              </button>
              {!currentGuide?.is_published ? (
                <button
                  className="btn-publish-guide"
                  onClick={handlePublish}
                  disabled={isSaving || !isGuideReady}
                >
                  Publish Guide
                </button>
              ) : (
                <div className="published-badge-inline">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8L6.5 11.5L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Published
                </div>
              )}
            </div>
          </div>

          <div className="preview-panel-controls">
            <div className="preview-mode-toggle">
              <label className="mode-toggle-label">
                <div className={`mode-toggle-switch ${theme !== 'dark' ? 'light' : 'dark'}`}>
                  <input
                    type="checkbox"
                    checked={theme === 'dark'}     // checked = dark
                    onChange={toggleTheme}
                  />
                  <span className="mode-toggle-slider"></span>
                </div>
                <span className="mode-toggle-text">
                  {theme === 'dark' ? 'Dark Theme' : 'Light Theme'}
                </span>
              </label>
            </div>
            <div className="preview-page-count">
              {totalPages > 0 ? `${totalPages} ${totalPages === 1 ? 'page' : 'pages'}` : 'No pages'}
            </div>
          </div>

          <div className="preview-content" ref={previewRef} id="live-preview-container">
            <LivePreview
              guide={currentGuide}
              theme={theme}
              currentPage={currentPage}
              onPageChange={goToPage}
              onAddStudy={!currentGuide?.is_published ? handleAddStudy : undefined}
              onGuideUpdate={handleGuideUpdate}
            />
          </div>
        </div>
      </div>
      </div>

      <Footer />

      {isSaving && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}

      {/* Publish Confirmation Modal */}
      {showPublishModal && (
        <div className="modal-overlay" onClick={cancelPublish}>
          <div className="modal-popup" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Ready to publish?</h2>
            <p className="modal-description">
              Publishing is final—no more edits can be made.
            </p>
            <div className="modal-actions">
              <button className="modal-btn modal-btn-primary" onClick={confirmPublish}>
                Publish guide
              </button>
              <button className="modal-btn modal-btn-link" onClick={cancelPublish}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Study Confirmation Modal */}
      {showDeleteStudyModal && (
        <div className="modal-overlay" onClick={cancelStudyDelete}>
          <div className="modal-popup" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Are you sure you want to delete?</h2>
            <p className="modal-description">
              Deleting means permanent removal and cannot be recovered.
            </p>
            <div className="modal-actions">
              <button className="modal-btn modal-btn-danger" onClick={confirmStudyDelete}>
                Delete study
              </button>
              <button className="modal-btn modal-btn-link" onClick={cancelStudyDelete}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Builder;